import type { AgentClient } from "@skaile/workspaces/client";
import type {
  AgentCommand,
  AgentEvent,
  ConnectorRequestCommand,
  ConnectorResponseEvent,
  Mention,
  ReplyRef,
} from "@skaile/workspaces/types";
import type { Message, MessageStore, SubscriberInfo, SubscriberTransport } from "./types.js";
import {
  renderWorkspaceChangeDigest,
  WorkspaceChangeAccumulator,
  type WorkspaceChangeReservation,
} from "./workspace-change-digest.js";

/**
 * Construction options for {@link SessionDispatcher}.
 *
 * All three infrastructure objects (`client`, `store`, `transport`) are
 * injected by the caller so the dispatcher remains testable and framework-agnostic.
 *
 * @docLink packages/session/api-reference#session-dispatcher
 */
export interface SessionDispatcherOptions {
  /** Unique identifier for the session this dispatcher manages. */
  sessionId: string;
  /** Connected agent client used to send commands and receive events. */
  client: AgentClient;
  /** Persistence layer for all non-transient messages. */
  store: MessageStore;
  /** Fan-out transport for broadcasting events to connected frontends. */
  transport: SubscriberTransport;
  /**
   * When true, the dispatcher prepends conversation history to every prompt
   * command sent to the agent. Enable for agents without native session
   * continuity (e.g. mock mode). Agents with native multi-turn support
   * (Claude SDK, OMP) should set this to false to avoid duplicate context.
   * Default: false.
   */
  injectConversationHistory?: boolean;
  /** Max conversation history messages to include in enriched prompts (default: 50). */
  historyLimit?: number;
  /**
   * Optional callback to transform prompt text before forwarding to the agent.
   * The original (untransformed) prompt is persisted and broadcast to subscribers.
   * Applied after conversation history enrichment. Use this to inject context
   * (e.g. UI state) that the agent should see but subscribers should not.
   */
  transformPrompt?: (prompt: string, senderId: string | undefined, replyTo?: ReplyRef) => string;
}

/**
 * Command and event types classified as transient RPC — high-frequency,
 * ephemeral, and not part of the conversation history.
 *
 * Matching payloads are forwarded and broadcast but never persisted
 * or assigned a sequence number, preventing bloat in the message log.
 */
const TRANSIENT_TYPES = new Set([
  "resource_request",
  "resource_response",
  "resources_available",
  "commands_available",
  "state_changed",
  "status",
  "typing",
  "user_message",
  // OMP session tracking — forwarded to subscribers but not persisted
  "session_info",
  // Structured log forwarding — high-volume; persisted in per-session SQLite, not the message store
  "log_entry",
  // Generic connector dispatch — system RPC, not chat messages.
  // Replaces the 6 typed flow commands removed in Protocol 2.2 (Phase 4 of the
  // flow-connector extraction). `connector_mutate` against the `flow` connector
  // is now how hosts drive flow execution between turns.
  "connector_mutate",
  "connector_query",
  "connector_query_response",
  // Mandatory, not cosmetic: `shouldPersist` is a pure denylist with no escape
  // hatch, so an unlisted ack is stored as a chat Message and flushes the
  // in-flight streaming-text buffer, splitting the assistant's bubble mid-turn.
  "connector_mutate_response",
  // Prompt receipt-ack (3.8). Same reasoning as connector_mutate_response:
  // system RPC, and an unlisted ack would split the assistant's bubble.
  "prompt_ack",
]);

/**
 * Bridges a single agent session between an {@link AgentClient} and one or more
 * frontend subscribers.
 *
 * Responsibilities:
 * - Persists every non-transient command and event as a {@link Message} with a
 *   monotonic sequence number via the injected {@link MessageStore}.
 * - Fans agent events out to all connected frontends via the injected
 *   {@link SubscriberTransport}.
 * - Optionally enriches prompt commands with conversation history so agents
 *   without native multi-turn support maintain context across turns.
 *
 * **Lifecycle:** call {@link init} before any other method, and {@link disconnect}
 * when the session ends.
 *
 * @example
 * ```ts
 * const dispatcher = new SessionDispatcher({
 *   sessionId: 'sess-123',
 *   client: agentClient,
 *   store: prismaMessageStore,
 *   transport: trpcTransport,
 * })
 * await dispatcher.init()
 * const history = await dispatcher.subscribe('ws-conn-abc', { limit: 50 })
 * const msg = await dispatcher.sendCommand({ type: 'prompt', prompt: 'Hello' }, 'user-42')
 * await dispatcher.disconnect()
 * ```
 *
 * @docLink packages/session/api-reference#session-dispatcher
 */
export class SessionDispatcher {
  /** The session identifier this dispatcher is bound to. */
  readonly sessionId: string;
  private client: AgentClient;
  private store: MessageStore;
  private transport: SubscriberTransport;
  private subscribers: Set<string> = new Set();
  private subscriberMeta: Map<string, SubscriberInfo> = new Map();
  private seq: number = 0;
  private _availableResources: { mounts: unknown[]; connectors: unknown[] } = {
    mounts: [],
    connectors: [],
  };

  /** Whether to inject conversation history into prompt commands. */
  private readonly injectConversationHistory: boolean;
  /** Maximum number of history messages to include in enriched prompts. */
  private readonly historyLimit: number;
  /** Optional prompt transformer applied before forwarding (after persistence). */
  private _transformPrompt?: (
    prompt: string,
    senderId: string | undefined,
    replyTo?: ReplyRef,
  ) => string;
  /** Whether the dispatcher is currently in compaction mode. */
  private _compacting = false;
  /** Accumulated text content during compaction. */
  private _compactionText = "";
  /** Idle watcher changes waiting for the next agent-visible delivery. */
  private readonly workspaceChanges = new WorkspaceChangeAccumulator();
  /** V1 turn boundary: delivery starts a turn; finished/fatal error ends it. */
  private agentTurnActive = false;
  /** True only during the synchronous AgentClient.send call. */
  private agentDeliveryInFlight = false;
  /** Failed idle deliveries retain synchronous watcher events for retry. */
  private deliveryStartedWhileIdle = false;
  /** Prevents a synchronous terminal event from being overwritten after send. */
  private deliveryReachedTerminal = false;
  /** Idle changes emitted after a synchronous terminal survive a successful send. */
  private postTerminalWorkspaceChanges: WorkspaceChangeAccumulator | null = null;

  /**
   * One-shot waiters registered via {@link onceNextFinished}. Each is invoked
   * exactly once with the text of the next `finished` assistant event, then
   * the list is cleared. Backs the A2A router's synchronous `ask` answer
   * capture.
   */
  private nextFinishedWaiters: Array<(text: string) => void> = [];
  /**
   * Text streamed by the agent since the last `finished` event. Used as the
   * fallback answer text for {@link onceNextFinished} when a `finished` event
   * carries an empty `summary` (drivers that stream `text` chunks and leave
   * the summary blank).
   */
  private _turnText = "";

  /**
   * @param opts - Dispatcher configuration — see {@link SessionDispatcherOptions}.
   */
  constructor(opts: SessionDispatcherOptions) {
    this.sessionId = opts.sessionId;
    this.client = opts.client;
    this.store = opts.store;
    this.transport = opts.transport;
    this.injectConversationHistory = opts.injectConversationHistory ?? false;
    this.historyLimit = opts.historyLimit ?? 50;
    this._transformPrompt = opts.transformPrompt;
  }

  /**
   * Replace the prompt transformer at runtime.
   *
   * Useful when additional context (e.g. a UI state cache) becomes available
   * after the dispatcher is constructed but before the first prompt is sent.
   */
  set transformPrompt(fn:
    | ((prompt: string, senderId: string | undefined, replyTo?: ReplyRef) => string)
    | undefined,) {
    this._transformPrompt = fn;
  }

  /**
   * Initialize the dispatcher for this session.
   *
   * Restores the monotonic sequence counter from the store and wires up the
   * agent event listener. Must be called once before any other method.
   *
   * @throws If {@link MessageStore.getLatestSeq} rejects.
   */
  async init(): Promise<void> {
    this.seq = await this.store.getLatestSeq(this.sessionId);
    this.client.onEvent((event: AgentEvent) => {
      void this.handleAgentEvent(event);
    });
  }

  /**
   * Register a frontend subscriber and return recent message history.
   *
   * Subsequent agent events are pushed to this subscriber via the
   * {@link SubscriberTransport} until {@link unsubscribe} is called.
   *
   * @param subscriberId - Opaque connection ID (e.g. WebSocket session ID).
   * @param opts - Optional subscription options.
   * @param opts.limit - Number of recent messages to return (default: 50).
   * @returns Recent messages ordered by seq ascending.
   */
  async subscribe(
    subscriberId: string,
    opts?: { limit?: number; userId?: string; roles?: string[] },
  ): Promise<Message[]> {
    this.subscribers.add(subscriberId);
    if (opts?.userId !== undefined || opts?.roles !== undefined) {
      this.subscriberMeta.set(subscriberId, {
        userId: opts.userId,
        roles: opts.roles,
      });
    }
    return this.store.getMessages(this.sessionId, {
      limit: opts?.limit ?? 50,
    });
  }

  /**
   * Remove a frontend subscriber and release transport resources.
   *
   * @param subscriberId - The ID passed to {@link subscribe}.
   */
  unsubscribe(subscriberId: string): void {
    this.subscribers.delete(subscriberId);
    this.subscriberMeta.delete(subscriberId);
    this.transport.remove(subscriberId);
  }

  /**
   * Send a command from a frontend to the agent.
   *
   * Persistent commands are wrapped in a Message envelope and stored before
   * forwarding. Transient commands (resource RPC) are forwarded directly
   * without persistence — returns null in that case.
   *
   * For `prompt` commands, the original message is persisted, but an enriched
   * version (with conversation history prepended) is sent to the agent so it
   * maintains context across turns.
   *
   * @param command - The command to dispatch to the agent (a prompt or tool result).
   * @param senderId - ID of the user sending the command, if known.
   * @param mentions - Resolved mention objects extracted from the prompt text.
   * @param visibility - Optional platform visibility envelope (F-23). When
   *   `visibilityMode` is `HumansOnly`, or `Private` without the `"__agent__"`
   *   sentinel in `privateRecipientIds` (B-30), the message is persisted and
   *   broadcast to subscribers but NOT forwarded to the agent.
   * @param outgoingPrefix - Optional text prepended to the outgoing command's
   *   user-text field ({@link command.prompt} / `command.answer` /
   *   `command.content`) AFTER persistence but BEFORE history enrichment and
   *   {@link transformPrompt}. The persisted {@link Message} envelope is built
   *   from the original `command` and never sees the prefix. Use this to
   *   inject one-shot wake-time context (Option B1 pending restoration
   *   prompt) that the agent must see but UI subscribers must not.
   * @returns The persisted Message envelope, or null for transient command types.
   */
  async sendCommand(
    command: AgentCommand,
    senderId?: string,
    mentions?: Mention[],
    visibility?: NonNullable<Message["visibility"]>,
    outgoingPrefix?: string,
  ): Promise<Message | null> {
    if (!this.shouldPersist(command)) {
      this.client.send(command);
      return null;
    }
    // Persist the original command (unenriched/untransformed/un-prefixed).
    // `outgoingPrefix` is intentionally NOT applied here — it must only
    // reach the agent client, never the MessageStore.
    const message = this.createMessage(command, senderId);
    // @TODO: Code-Review: v1 back-compat — mentions field no longer used by Protocol v2+ persistence layers; remove once platform PrismaMessageStore is migrated
    if (mentions?.length) message.mentions = mentions;
    // F-23: platform-extension visibility metadata (3.2.0). The platform
    // MessageStore reads this off the envelope and writes the
    // Message.visibilityMode column + recipient rows atomically with
    // the message itself — no post-send patch, no race window where a
    // concurrent send could mis-tag the wrong message id.
    if (visibility) message.visibility = visibility;
    await this.store.append(message);

    // Build the outgoing command — enrich and transform prompt commands
    // while leaving the persisted message untouched.
    let outgoing: AgentCommand = command;
    if (command.type === "prompt") {
      let prompt = command.prompt;
      // Restoration prefix lands FIRST (innermost) so the user's typed
      // text remains adjacent to its own framing, with history and reply
      // preambles wrapping the combined block from the outside in.
      if (outgoingPrefix) {
        prompt = `${outgoingPrefix}${prompt}`;
      }
      if (this.injectConversationHistory) {
        prompt = await this.enrichPromptWithHistory(prompt);
      }
      if (this._transformPrompt) {
        prompt = this._transformPrompt(prompt, senderId, command.replyTo);
      }
      if (prompt !== command.prompt) {
        outgoing = { ...command, prompt };
      }
    } else if (outgoingPrefix && command.type === "reply") {
      // `reply` carries user text in `answer`. Prefix is invisible to
      // subscribers (who see the persisted Message.payload.answer) but
      // visible to the agent driver.
      outgoing = { ...command, answer: `${outgoingPrefix}${command.answer}` };
    } else if (outgoingPrefix && command.type === "custom_message") {
      // `custom_message` carries user text in `content`. Same persist/send
      // split as the `reply` branch above.
      outgoing = { ...command, content: `${outgoingPrefix}${command.content}` };
    }

    // F-23 P3 review (B1) — HumansOnly visibility means the message is
    // intended for human recipients only; the agent must NOT see the
    // prompt content. Persist + broadcast (humans read it via
    // SubscriberTransport) but skip the agent forward. This closes a
    // critical privacy hole where `@user: secret` was persisted with
    // visibilityMode='HumansOnly' yet still reached the LLM. Threads
    // starting HumansOnly stay agent-invisible even on Public follow-ups
    // (parent-thread visibility threading is intentionally out of scope
    // for v1; only the immediate message is gated). See
    // platform/_concept/plans/f-23-p3-review-blockers.md.
    if (visibility?.visibilityMode === "HumansOnly") {
      return message;
    }

    // B-30 — Private visibility (F-23 `@mention_` underscore suffix) means the
    // message is addressed to an explicit recipient set. The agent is only a
    // recipient when the sender wrote `@agent_`, which the platform encodes as
    // the `"__agent__"` sentinel in `privateRecipientIds`. When the sentinel is
    // absent the message is a private human-to-human exchange: persist +
    // broadcast (the named human recipients read it via SubscriberTransport)
    // but skip the agent forward so the LLM never sees private content.
    if (
      visibility?.visibilityMode === "Private" &&
      !visibility.privateRecipientIds?.includes("__agent__")
    ) {
      return message;
    }

    if (this.isAgentVisibleCommand(outgoing)) {
      this.sendAgentVisible(outgoing);
    } else {
      this.client.send(outgoing);
    }
    return message;
  }

  /**
   * Persist a synthetic event (not received from the agent) and broadcast it.
   *
   * Used by the platform to inject events like a partial-text `finished` record
   * before a cancel command.
   *
   * @param event - The agent event to persist.
   * @param mentions - Resolved mentions from the originating command.
   */
  async persistEvent(event: AgentEvent, mentions?: Mention[]): Promise<Message | null> {
    return this.handleAgentEvent(event, mentions);
  }

  /**
   * Load paginated message history for this session.
   *
   * @param opts.before - Return messages with seq strictly less than this value.
   * @param opts.limit - Maximum number of messages to return.
   */
  async getMessages(opts?: { before?: number; limit?: number }): Promise<Message[]> {
    return this.store.getMessages(this.sessionId, opts);
  }

  /**
   * Available mounts and connectors captured from the most recent
   * `resources_available` event emitted by the agent on connect.
   *
   * Empty until the agent emits the event or {@link seedResources} is called.
   */
  get availableResources(): { mounts: unknown[]; connectors: unknown[] } {
    return this._availableResources;
  }

  /**
   * Seed the available resources from an external cache.
   *
   * This is needed when the dispatcher is created after the agent has already
   * emitted `resources_available` on WebSocket connect. Without seeding, the
   * dispatcher would report an empty resource list until the next reconnect.
   *
   * @param resources - Resource handles to register before the session starts.
   */
  seedResources(resources: { mounts: unknown[]; connectors: unknown[] }): void {
    const empty =
      this._availableResources.mounts.length === 0 &&
      this._availableResources.connectors.length === 0;
    if (empty && (resources.mounts.length > 0 || resources.connectors.length > 0)) {
      this._availableResources = resources;
    }
  }

  /**
   * Deliver a prompt to the agent that triggers a turn but is NOT persisted.
   *
   * Unlike {@link sendCommand}, this never touches the {@link MessageStore}:
   * the prompt is forwarded straight to the agent client. The caller owns
   * persistence of whatever conversation rows should represent this turn.
   *
   * Used by the platform's A2A router to deliver a `from-peer` prompt to a
   * target session: the router writes the authoritative A2A `Message` rows
   * itself, so persisting the framed prompt here too would create an extra,
   * un-attributed plain `prompt` row. The dispatcher's job for this prompt is
   * delivery only.
   *
   * The agent still produces a normal turn, so an A2A `ask` round-trip can
   * capture the answer via {@link onceNextFinished}.
   *
   * @param prompt - The fully-formed prompt text to deliver to the agent.
   */
  deliverPrompt(prompt: string): void {
    this.sendAgentVisible({ type: "prompt", prompt });
  }

  /**
   * Send a resource request and await the correlated response.
   *
   * This is a convenience method for request/response resource RPC.
   * The request is transient (not persisted). A one-shot event listener
   * waits for the matching `resource_response` by `requestId`.
   *
   * @param command - A ConnectorRequestCommand (caller must set requestId).
   * @param timeoutMs - How long to wait before rejecting (default: 30s).
   */
  async requestResource(
    command: ConnectorRequestCommand,
    timeoutMs = 30_000,
  ): Promise<ConnectorResponseEvent> {
    return new Promise<ConnectorResponseEvent>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.client.offEvent(handler);
        reject(
          new Error(
            `Resource request timed out after ${timeoutMs}ms: ${command.operation} ${command.connectorId}`,
          ),
        );
      }, timeoutMs);

      const handler = (event: AgentEvent) => {
        if (event.type === "resource_response" && event.requestId === command.requestId) {
          clearTimeout(timer);
          this.client.offEvent(handler);
          resolve(event);
        }
      };

      this.client.onEvent(handler);
      this.client.send(command);
    });
  }

  /**
   * Broadcast a transient event to all subscribers without persisting it.
   *
   * Used for ephemeral signals like typing indicators or user-command
   * echoes that other subscribers need to see in real time.
   */
  broadcast(event: AgentEvent): void {
    for (const subscriberId of this.subscribers) {
      this.transport.send(subscriberId, event);
    }
  }

  /** Disconnect the agent client and clean up all subscribers. */
  async disconnect(): Promise<void> {
    for (const id of this.subscribers) {
      this.transport.remove(id);
    }
    this.subscribers.clear();
    this.subscriberMeta.clear();
    // Clear any pending A2A waiters so they don't leak if the session
    // disconnects before the target ever emits a `finished` event.
    this.nextFinishedWaiters = [];
    await this.client.dispose();
  }

  /**
   * Enter compaction mode. While active, events from the agent are captured
   * but NOT persisted. Text content is accumulated for the snapshot.
   */
  beginCompaction(): void {
    this._compacting = true;
    this._compactionText = "";
  }

  /**
   * Exit compaction mode and return the accumulated text.
   * The caller is responsible for creating and emitting the snapshot event.
   */
  endCompaction(): string {
    this._compacting = false;
    const text = this._compactionText;
    this._compactionText = "";
    return text;
  }

  /** Whether the dispatcher is currently in compaction mode. */
  get isCompacting(): boolean {
    return this._compacting;
  }

  /**
   * Register a one-shot callback fired with the text of this session's next
   * finished assistant turn. Returns an unsubscribe function.
   *
   * Semantics:
   * - The callback fires **at most once** — on the first `finished` event
   *   observed after registration. Later finished turns do not re-fire it.
   * - A waiter registered after a `finished` event waits for the *following*
   *   one. The hook captures the *next* finished turn, not a past one.
   * - The answer text is the `finished` event's `summary`; when that is
   *   empty (drivers that stream `text` and leave the summary blank), the
   *   text accumulated since the previous finished turn is used instead.
   * - Calling the returned function removes the waiter; if it has already
   *   fired this is a no-op.
   *
   * Used by the platform's A2A router (`A2ARouterService.ask`) to capture a
   * peer session's answer to a `from-peer` question. v1 captures the first
   * finished turn after delivery — see the busy-target notes in the A2A
   * design spec for the known turn-matching limitation.
   *
   * @param cb - Invoked once with the next finished turn's text.
   * @returns Unsubscribe function that removes this waiter.
   */
  onceNextFinished(cb: (text: string) => void): () => void {
    this.nextFinishedWaiters.push(cb);
    return () => {
      const idx = this.nextFinishedWaiters.indexOf(cb);
      if (idx !== -1) {
        this.nextFinishedWaiters.splice(idx, 1);
      }
    };
  }

  // -- Private ----------------------------------------------------------------

  /**
   * Build an enriched prompt by prepending conversation history.
   *
   * Loads recent persisted messages, formats user/assistant turns, and
   * wraps them in a `<conversation_history>` block. The current user
   * message (just persisted) is excluded since it is already the prompt.
   *
   * Each turn is rendered as a self-closing `<turn role="...">` element
   * rather than a bare `[User]:` / `[Assistant]:` transcript line. A plain
   * role-prefixed transcript invites the model to *continue* it — completing
   * the next `[User]:` turn as plaintext inside its own answer (a fabricated
   * follow-up that swallows the real next message). XML-tagged, explicitly
   * read-only turns remove that completion affordance while preserving the
   * same information.
   *
   * Returns the original prompt unchanged if there is no prior history.
   */
  private async enrichPromptWithHistory(currentPrompt: string): Promise<string> {
    const messages = await this.store.getMessages(this.sessionId, {
      limit: this.historyLimit,
    });

    // Filter to conversation-relevant types and exclude the message we just appended
    const history = messages
      .filter((m) => {
        const type = m.payload.type;
        return type === "prompt" || type === "finished" || type === "question";
      })
      .slice(0, -1); // Exclude the current message (last by seq)

    if (history.length === 0) {
      return currentPrompt;
    }

    // Escape so message content (which may itself contain `</turn>` or other
    // angle brackets) cannot break out of the XML structure or inject a forged
    // turn. The model reads entity-encoded text fine; this is read-only context.
    const esc = (s: string): string =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const turns = history.map((m) => {
      const p = m.payload as Record<string, unknown>;
      if (p.type === "prompt") {
        return `<turn role="user">${esc(String(p.prompt ?? ""))}</turn>`;
      }
      // finished (assistant response) or question
      const content = String(p.summary ?? p.question ?? "");
      return `<turn role="assistant">${esc(content)}</turn>`;
    });

    return (
      "<conversation_history>\n" +
      "The following is prior conversation context, provided read-only so you " +
      "have continuity. Do NOT continue, echo, or complete it — it is history, " +
      "not a turn for you to extend. Respond only to the user message that " +
      "follows the closing tag.\n" +
      `${turns.join("\n")}\n` +
      "</conversation_history>\n\n" +
      currentPrompt
    );
  }

  private async handleAgentEvent(event: AgentEvent, mentions?: Mention[]): Promise<Message | null> {
    if (event.type === "finished" || (event.type === "error" && event.fatal)) {
      this.agentTurnActive = false;
      if (this.agentDeliveryInFlight) this.deliveryReachedTerminal = true;
    }
    if (event.type === "file_changed") {
      const captureForFailedDelivery = this.agentDeliveryInFlight && this.deliveryStartedWhileIdle;
      if (!this.agentTurnActive || captureForFailedDelivery) {
        this.workspaceChanges.add(event);
      }
      if (this.agentDeliveryInFlight && this.deliveryReachedTerminal) {
        this.postTerminalWorkspaceChanges?.add(event);
      }
    }

    // ── Compaction mode: capture text but don't persist ─────────────────
    if (this._compacting) {
      if (event.type === "text") {
        this._compactionText += (event as { content: string }).content;
      }
      // Still broadcast to subscribers (UI shows compaction progress)
      for (const subscriberId of this.subscribers) {
        this.transport.send(subscriberId, event);
      }
      return null;
    }

    // Capture resource metadata when the server announces available resources
    if (event.type === "resources_available") {
      const payload = event as { mounts?: unknown[]; connectors?: unknown[] };
      this._availableResources = {
        mounts: payload.mounts ?? [],
        connectors: payload.connectors ?? [],
      };
    }

    // ── onceNextFinished — one-shot "next finished turn" answer capture ──
    // Accumulate streamed text across the turn so the answer is available
    // even when a driver emits an empty `finished.summary`. On `finished`,
    // drain every queued waiter exactly once, then reset the accumulator.
    if (event.type === "text") {
      this._turnText += (event as { content: string }).content;
    } else if (event.type === "finished") {
      const summary = (event as { summary?: string }).summary ?? "";
      const answer = summary.length > 0 ? summary : this._turnText;
      this._turnText = "";
      if (this.nextFinishedWaiters.length > 0) {
        const waiters = this.nextFinishedWaiters;
        this.nextFinishedWaiters = [];
        for (const cb of waiters) {
          cb(answer);
        }
      }
    }

    let message: Message | null = null;
    if (this.shouldPersist(event)) {
      message = this.createMessage(event);
      if (mentions?.length) message.mentions = mentions;
      await this.store.append(message);
    }

    for (const subscriberId of this.subscribers) {
      this.transport.send(subscriberId, event);
    }
    return message;
  }

  private shouldPersist(payload: AgentCommand | AgentEvent): boolean {
    return !TRANSIENT_TYPES.has(payload.type);
  }

  private isAgentVisibleCommand(
    command: AgentCommand,
  ): command is Extract<AgentCommand, { type: "prompt" | "reply" | "custom_message" }> {
    return (
      command.type === "prompt" || command.type === "reply" || command.type === "custom_message"
    );
  }

  private sendAgentVisible(
    command: Extract<AgentCommand, { type: "prompt" | "reply" | "custom_message" }>,
  ): void {
    if (this.agentDeliveryInFlight) {
      throw new Error("Nested synchronous agent-visible delivery is not supported");
    }
    if (this.client.isRunning === false) {
      throw new Error("Cannot deliver an agent-visible command while the agent client is stopped");
    }

    const reservation = this.workspaceChanges.reserve();
    const outgoing = reservation ? this.prependWorkspaceChanges(command, reservation) : command;
    const wasActive = this.agentTurnActive;
    this.agentDeliveryInFlight = true;
    this.deliveryStartedWhileIdle = !wasActive;
    this.deliveryReachedTerminal = false;
    this.postTerminalWorkspaceChanges = new WorkspaceChangeAccumulator();
    this.agentTurnActive = true;
    try {
      this.client.send(outgoing);
      this.workspaceChanges.replace(this.postTerminalWorkspaceChanges.reserve());
    } catch (error) {
      if (!this.deliveryReachedTerminal) this.agentTurnActive = wasActive;
      throw error;
    } finally {
      this.agentDeliveryInFlight = false;
      this.deliveryStartedWhileIdle = false;
      this.deliveryReachedTerminal = false;
      this.postTerminalWorkspaceChanges = null;
    }
  }

  private prependWorkspaceChanges(
    command: Extract<AgentCommand, { type: "prompt" | "reply" | "custom_message" }>,
    reservation: WorkspaceChangeReservation,
  ): Extract<AgentCommand, { type: "prompt" | "reply" | "custom_message" }> {
    const prefix = `${renderWorkspaceChangeDigest(reservation)}\n\n`;
    if (command.type === "prompt") return { ...command, prompt: `${prefix}${command.prompt}` };
    if (command.type === "reply") return { ...command, answer: `${prefix}${command.answer}` };
    return { ...command, content: `${prefix}${command.content}` };
  }

  private createMessage(payload: AgentCommand | AgentEvent, senderId?: string): Message {
    this.seq++;
    return {
      id: crypto.randomUUID(),
      seq: this.seq,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      ...(senderId ? { senderId } : {}),
      payload,
    };
  }
}
